import { CANVAS_CONFIG } from './constants.js';

/**
 * LadderAnimator - Handles animation of ball moving through ladder
 */
export class LadderAnimator {
  constructor(path, duration = CANVAS_CONFIG.animationDuration) {
    this.path = path;
    this.duration = duration;
    this.startTime = null;
    this.animationId = null;
    this.onUpdate = null;
    this.onComplete = null;
  }

  /**
   * Start animation
   * @param {Function} onUpdate - Callback called on each frame with (position, progress)
   * @param {Function} onComplete - Callback called when animation completes
   */
  start(onUpdate, onComplete) {
    this.onUpdate = onUpdate;
    this.onComplete = onComplete;
    this.startTime = performance.now();

    const animate = (currentTime) => {
      const elapsed = currentTime - this.startTime;
      const progress = Math.min(elapsed / this.duration, 1);

      // Call update callback
      if (this.onUpdate) {
        const position = this.getPositionAtProgress(progress);
        this.onUpdate(position, progress);
      }

      // Check if animation is complete
      if (progress >= 1) {
        if (this.onComplete) {
          this.onComplete();
        }
        return;
      }

      // Schedule next frame
      this.animationId = requestAnimationFrame(animate);
    };

    this.animationId = requestAnimationFrame(animate);
  }

  /**
   * Stop animation
   */
  stop() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  /**
   * Get position at given progress (0-1)
   * @param {number} progress - Progress value from 0 to 1
   * @returns {Object} {x, y, type}
   */
  getPositionAtProgress(progress) {
    const easeProgress = this.easeOutCubic(progress);
    const pathIndex = Math.floor(easeProgress * (this.path.length - 1));

    // Clamp to valid range
    const index = Math.min(Math.max(pathIndex, 0), this.path.length - 1);

    return this.path[index];
  }

  /**
   * Ease-out cubic easing function
   * @param {number} t - Progress from 0 to 1
   * @returns {number} Eased progress
   */
  easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }
}
